import React, { useState, } from "react";

// Importing bootstrap components
import { Col, Card, Form, Button, Spinner } from "react-bootstrap";

// Importing formik & yup
import { Formik } from "formik";
import * as Yup from "yup";

// Importing toast utilities
import { showSuccessToast } from "utils/toast";

import { useDispatch, useSelector } from "react-redux";

// Importing designation slices
import {
  addDesignation as addDesignationToStore,
  updateDesignation as updateDesignationInStore,
  deleteDesignation as deleteDesignationFromStore,
} from "state/slices/designation/designationSlice";

import {
  useCreateDesignationMutation,
  useUpdateDesignationMutation,
  useDeleteDesignationMutation,
} from "state/slices/designation/designationApiSlice";

// Importing user slices
import { updateUserInfo } from "state/slices/auth/authSlice";

// Import Utilities
import { isUserOwnTheData } from "utils/authentication";

const initialValues = {
  id: null,
  name: "",
  isDefault: "",
};

const GeneralAccessManagement = React.memo(
  ({
    isAllowedToRead,
    isAllowedToCreate,
    isAllowedToUpdate,
    isAllowedToDelete,
    DesignationViewManagement,
  }) => {
    const [selectedRow, setSelectedRow] = useState(null);
    const [formikValues, setFormikValues] = useState(initialValues);

    const dispatch = useDispatch();

    const { userInfo: loggedUserInfo } = useSelector((state) => state.auth);

    const [createDesignation] = useCreateDesignationMutation();
    const [updateDesignation] = useUpdateDesignationMutation();
    const [deleteDesignation] = useDeleteDesignationMutation();

    // Function to add a new designation to designations
    const addDesignationToLocal = (designation) => {
      dispatch(addDesignationToStore(designation));
    };

    // Function to update a designation in designations
    const updateDesignationInLocal = (updatedDesignation) => {
      dispatch(updateDesignationInStore(updatedDesignation));
    };

    // Function to delete a designation from designations
    const deleteDesignationFromLocal = (designation) => {
      dispatch(deleteDesignationFromStore(designation));
    };

    const handleCheckboxChange = (event, row) => {
      if (event.target.checked) {
        setSelectedRow(row);
      } else {
        setSelectedRow(null);
        setFormikValues(initialValues);
      }
    };

    const handleEdit = (row) => {
      setFormikValues(row);
    };

    const addDesignation = async (designationData) => {
      try {
        const data = await createDesignation(designationData).unwrap();
        addDesignationToLocal(data);
        showSuccessToast("Designation added successfully");
      } catch (err) {
        // Error toast is automatically shown by baseQuery
      }
    };

    const updateDesignationManage = async (designation, designationData) => {
      try {
        const data = await updateDesignation({
          id: designation,
          data: designationData,
        }).unwrap();

        const updatedDesignation = {
          id: data?.designation.id,
          name: data?.designation.name,
          isDefault: data?.designation.defaultFlag,
        };

        updateDesignationInLocal(updatedDesignation);

        // Update designation of current user
        if (
          isUserOwnTheData(
            loggedUserInfo,
            "designation",
            data?.designation.id
          )
        ) {
          dispatch(
            updateUserInfo({
              field: "designation",
              data: data.designation,
            })
          );
        }

        showSuccessToast("Designation updated successfully");
      } catch (err) {
        // Error toast is automatically shown by baseQuery
      }
    };

    const deleteDesignationManage = async () => {
      try {
        await deleteDesignation(selectedRow.id).unwrap();
        deleteDesignationFromLocal(selectedRow.id);
        showSuccessToast("Designation deleted successfully");

        if (selectedRow.id) {
          setFormikValues(initialValues);
          setSelectedRow(null);
        }
      } catch (err) {
        // Error toast is automatically shown by baseQuery
      }
    };

    const handleSubmit = (values, { resetForm }) => {
      let isDefault =
        values.isDefault === "true" || values.isDefault === true ? true : false;

      const updatedValues = {
        ...values,
        isDefault,
      };

      if (formikValues.id) {
        updateDesignationManage(formikValues.id, updatedValues);
      } else {
        addDesignation(updatedValues);
      }

      if (selectedRow?.id) {
        setSelectedRow(null);
      }

      // Reset formik values
      setFormikValues(initialValues);

      // Reset form data
      resetForm();
    };

    const validationSchema = Yup.object().shape({
      name: Yup.string().required(`Designation name is required`),
      isDefault: Yup.string().required("Default Designation is required"),
    });

    return (
      <section className="section dashboard">
        {(isAllowedToCreate || isAllowedToUpdate) && (
          <Card>
            <Card.Body>
              <Formik
                enableReinitialize={true}
                initialValues={formikValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ values, handleChange, handleSubmit, touched, errors, isSubmitting }) => (
                  <Form noValidate onSubmit={handleSubmit} className="row mt-3">
                    <Col md={6} className="mb-3 mb-md-0">
                      <Form.Group controlId="name">
                        <Form.Control
                          type="text"
                          name="name"
                          placeholder={`Enter designation name`}
                          value={values.name}
                          onChange={handleChange}
                          isInvalid={touched.name && !!errors.name}
                          disabled={isSubmitting}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.name}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={4} className="mb-3 mb-md-0">
                      <Form.Group controlId="isDefault">
                        <Form.Control
                          as="select"
                          name="isDefault"
                          value={values.isDefault}
                          onChange={handleChange}
                          isInvalid={touched.isDefault && !!errors.isDefault}
                          disabled={isSubmitting}
                        >
                          <option value="">Set as default designation</option>
                          <option value={false}>No</option>
                          <option value={true}>Yes</option>
                        </Form.Control>
                        <Form.Control.Feedback type="invalid">
                          {errors.isDefault}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Button variant="primary" className="w-100" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                          <>
                            <Spinner
                              animation="border"
                              size="sm"
                              role="status"
                              className="me-2"
                              style={{ width: "18px", height: "18px" }}
                            />
                            {formikValues.id ? "Updating..." : "Adding..."}
                          </>
                        ) : (
                          formikValues.id ? "Save" : "Add"
                        )}
                      </Button>
                    </Col>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        )}
        {isAllowedToRead && (
          <DesignationViewManagement
            isAllowedToUpdate={isAllowedToUpdate}
            isAllowedToDelete={isAllowedToDelete}
            handleCheckboxChange={handleCheckboxChange}
            handleEdit={handleEdit}
            deleteDesignationManage={deleteDesignationManage}
            selectedRow={selectedRow}
          />
        )}
      </section>
    );
  }
);

export default GeneralAccessManagement;
